import ArrowBack from "@mui/icons-material/ArrowBack";
import { Box, Button, type ButtonProps, IconButton, Typography } from "@mui/material";
import { type FC, type PropsWithChildren, useCallback } from "react";
import { useFormStatus } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { SigninLogo } from "../../components/SigninLogo/SigninLogo";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { INK } from "../../theme/tokens";

// OTP length, shared by both signin OTP flows (Apple SSO + password-less login). Must match
// the backend generator/validators (backend model OTP_CODE_LENGTH).
export const OTP_CODE_LENGTH = 6;

/**
 * Shared OTP-code guard for both signin OTP flows. Returns a validator that shows the
 * "enter the N-digit code" snackbar and returns false when the code isn't exactly OTP_CODE_LENGTH.
 * Keeps the length rule + message in one place so both call sites stay in sync.
 */
export const useOtpCodeValidator = (): ((code: string | undefined) => boolean) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	return useCallback(
		(code: string | undefined): boolean => {
			if (code?.length !== OTP_CODE_LENGTH) {
				showSnackbar(t("please.fill.the.verification.code", { length: OTP_CODE_LENGTH }), "error");
				return false;
			}
			return true;
		},
		[t, showSnackbar],
	);
};

/**
 * Accepts the full Button prop surface so callers can pass `data-testid` and friends. The previous
 * narrow `{ children, disabled }` signature silently swallowed them — TypeScript does not
 * excess-property-check hyphenated JSX attributes, so every `<SubmitButton data-testid=...>` in the
 * signin flow compiled cleanly while rendering a button with no testid, which broke the Playwright
 * admin-auth setup (and with it every admin sanity/demo run).
 * `type`/`variant`/`loading` stay after the spread: they define what a SubmitButton *is*.
 */
export const SubmitButton: FC<ButtonProps> = ({ children, disabled = false, ...rest }) => {
	const { pending } = useFormStatus();
	return (
		<Button {...rest} type="submit" variant="contained" loading={pending} disabled={disabled}>
			{children}
		</Button>
	);
};

export const SigninLayout: FC<PropsWithChildren<{ title: string }>> = ({ children, title }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const isLoginPage = location.pathname === "/login";

	return (
		<Box
			sx={{
				width: "100%",
				// Fixed 100dvh (not minHeight) so the form scrolls inside this box
				// when the keyboard shrinks the viewport, instead of being clipped.
				height: "100dvh",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				overflowY: "auto",
			}}
		>
			{!isLoginPage && (
				<IconButton onClick={() => navigate(-1)} sx={{ position: "absolute", top: 10, left: 10 }}>
					<ArrowBack fontSize="large" />
				</IconButton>
			)}
			<Box
				component="main"
				sx={{
					width: "min(400px, 100%)",
					display: "flex",
					flexDirection: "column",
					gap: "0.5rem",
					alignItems: "stretch",
					px: "0.5rem",
					py: "1.5rem",
					// Centers the form when it fits; allows scrolling when it doesn't.
					my: "auto",
					position: "relative",
				}}
			>
				<Box component={RouterLink} to="/login" sx={{ alignSelf: "center" }}>
					<SigninLogo />
				</Box>
				<Typography
					component="h1"
					sx={{
						alignSelf: "center",
						fontWeight: 400,
						fontSize: "18px",
						py: "2rem",
						color: INK.SECONDARY,
					}}
				>
					{title}
				</Typography>

				{children}
			</Box>
		</Box>
	);
};
